import { Eye, EyeOff } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface TextInputProps {
  label: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: 'text' | 'password' | 'tel' | 'email' | 'number' | 'time' | 'url';
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric' | 'decimal';
  required?: boolean;
  disabled?: boolean;
  /** Monospace + LTR: phone numbers, codes, identifiers. */
  mono?: boolean;
  size?: 'md' | 'lg';
  name?: string;
  maxLength?: number;
  min?: number;
  max?: number;
  hint?: ReactNode;
  /** The server's translated issue for this field: shown in place of the hint, and the field is marked invalid. */
  error?: ReactNode;
  /** Fixed direction for text in one script (an Arabic title in the English panel). */
  dir?: 'rtl' | 'ltr';
  readOnly?: boolean;
}

export function TextInput({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
  inputMode,
  required,
  disabled,
  mono = false,
  size = 'md',
  name,
  maxLength,
  min,
  max,
  hint,
  error,
  dir,
  readOnly,
}: TextInputProps) {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  const note = error ?? hint;
  // Password fields get a show/hide toggle; the field stays type="password" for autofill until revealed.
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  const classes = ['fs-field__input'];
  if (size === 'lg') classes.push('fs-field__input--lg');
  if (mono) classes.push('fs-field__input--mono');
  const input = (
    <input
      id={id}
      className={classes.join(' ')}
      type={isPassword && revealed ? 'text' : type}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      autoComplete={autoComplete}
      inputMode={inputMode}
      required={required}
      disabled={disabled}
      name={name}
      maxLength={maxLength}
      min={min}
      max={max}
      dir={dir}
      readOnly={readOnly}
      aria-invalid={error ? true : undefined}
      aria-describedby={note ? hintId : undefined}
    />
  );
  return (
    <div className={error ? 'fs-field fs-field--invalid' : 'fs-field'}>
      <label className="fs-field__label" htmlFor={id}>
        {label}
      </label>
      {isPassword ? (
        <span className="fs-field__password">
          {input}
          <button
            type="button"
            className="fs-field__reveal"
            onClick={() => setRevealed((r) => !r)}
            disabled={disabled}
            aria-label={revealed ? t('common.hidePassword') : t('common.showPassword')}
            aria-controls={id}
          >
            {revealed ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
          </button>
        </span>
      ) : (
        input
      )}
      {note && (
        <span id={hintId} className="fs-field__hint">
          {note}
        </span>
      )}
    </div>
  );
}
